import className from './styles.module.css';
import TextLink from '../../components/common/TextLink';
import Button from '../../components/common/Button';
import { usePostLoginMutation } from '../../api/loginApi';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import {
  max,
  mistakeMailaddressMessage,
  requiredByMailaddressMessage,
} from '../../message';
import { useDispatch } from 'react-redux';
import { setTokens } from '../../redux/tokenSlice';
import FormBlockItem from '../../components/common/FormBlockItem';
import LoginLayout from '../../components/LoginLayout';
import { AppDispatch } from '../../redux/store';
import { useEffect, useRef, useState } from 'react';
import { getMaintenanceInfo } from '../../api/maintenance';
import { MaintenanceInfo } from '../../types/maintenance';
import { logger } from '../../services/logger';
import MaintenanceNotice from '../../components/common/MaintenanceNotice';
import { setMaintenanceInfo } from '../../redux/maintenanceSlice';
import { createPasswordRegisterOptions } from '../../validators/password';
import { enqueueError } from '../../redux/errorSlice';

const passwordRegisterOptions = createPasswordRegisterOptions('パスワード');

function Login() {
  const navigate = useNavigate();

  const MailaddressValidation = {
    required: requiredByMailaddressMessage,
    maxLength: {
      value: 255,
      message: max('メールアドレス', 255),
    },
    validate: (value: string) => {
      return value.indexOf('@') !== -1 || mistakeMailaddressMessage;
    },
  };

  const [postLogin] = usePostLoginMutation();
  const dispatch = useDispatch<AppDispatch>();

  const form = useForm({ mode: 'onBlur' });

  const { register } = form;

  const notice = useMaintenanceNotice();

  return (
    <LoginLayout
      announce={notice ? <MaintenanceNotice notice={notice} /> : undefined}
    >
      <form
        onSubmit={form.handleSubmit(async (data) => {
          await postLogin({
            email: data.email,
            password: data.password,
          })
            .unwrap()
            .then((responseData) => {
              dispatch(
                setTokens({
                  accessToken: responseData?.accessToken,
                  refreshToken: responseData?.refreshToken,
                })
              );
              navigate('/');
            })
            .catch((err) => {
              dispatch(
                enqueueError(
                  [400, 401].includes(err?.data?.code)
                    ? // API ログインエラーでは入力エラーとして扱われるようにステータスコードを 400 に書き換える
                      // @see https://fenrir-inc.backlog.com/view/MRBN_UR_INHOUSE-729#comment-322642436
                      {
                        status: 400,
                        data: { ...err?.data, code: 400 },
                      }
                    : // それ以外は通常のエラーとして扱う
                      err
                )
              );
            });
        })}
      >
        <div>
          <div>
            <FormBlockItem
              label="メールアドレス(ログインID)"
              type="email"
              placeholder="you@example.com"
              autoComplete="email"
              {...register('email', MailaddressValidation)}
              error={
                form.formState.errors.email
                  ? String(form.formState.errors.email.message)
                  : undefined
              }
            />
            <FormBlockItem
              label="パスワード"
              type="password"
              autoComplete="current-password"
              {...register('password', passwordRegisterOptions)}
              error={
                form.formState.errors.password
                  ? String(form.formState.errors.password.message)
                  : undefined
              }
            />
          </div>
          <div className={className.PasswordReset}>
            <TextLink to={'./password-reset-mail'}>
              パスワードをお忘れの方
            </TextLink>
          </div>
        </div>
        <div className={className.LoginButton}>
          <Button
            variants={['fit']}
            type="submit"
            disabled={!form.formState.isValid}
          >
            ログイン
          </Button>
        </div>
      </form>
    </LoginLayout>
  );
}

export default Login;

function useMaintenanceNotice() {
  const [notice, setNotice] = useState<MaintenanceInfo['notice']>(null);
  const dispatch = useDispatch<AppDispatch>();
  useDidMount(() => {
    getMaintenanceInfo()
      .then((info) => {
        if (info.isMaintenance) dispatch(setMaintenanceInfo(info));
        if (info.showNotice) setNotice(info.notice);
      })
      .catch(logger.error);
  });
  return notice;
}

/**
 * コンポーネントがマウントされたときに処理を実行する
 * @param fn 実行する処理
 */
function useDidMount(fn: () => void) {
  const ref = useRef(false);
  useEffect(() => {
    if (ref.current) return;
    ref.current = true;
    fn();
  }, []);
}
